import Editor, { OnMount } from "@monaco-editor/react";
import { useTheme } from "next-themes";
import { useEffect, useRef } from "react";

interface CodeViewerProps {
  code: string;
  height?: string;
  maxHeight?: string;
}

export const CodeViewer = ({ code, height, maxHeight = "400px" }: CodeViewerProps) => {
  const { theme } = useTheme();
  const monacoRef = useRef<any>(null);
  
  // If height is explicitly provided, use it; otherwise calculate from line count
  const editorHeight = height ? height : (() => {
    const lineCount = code.split('\n').length;
    const lineHeight = 23;
    const padding = 32;
    const calculatedHeight = Math.min(lineCount * lineHeight + padding, parseInt(maxHeight));
    return `${calculatedHeight}px`;
  })();
  
  const handleEditorDidMount: OnMount = (editor, monaco) => {
    monacoRef.current = monaco;
    
    // Define custom light theme with light gray background
    monaco.editor.defineTheme('card-light', {
      base: 'vs',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#f5f5f5', // light gray muted
      }
    });
    
    // Define custom dark theme matching card background
    monaco.editor.defineTheme('card-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#151820', // --card dark (hsl(222, 14%, 10%))
      }
    });
    
    // Apply the appropriate theme
    monaco.editor.setTheme(theme === 'dark' ? 'card-dark' : 'card-light');
  };
  
  // Update theme when it changes
  useEffect(() => {
    if (monacoRef.current) {
      monacoRef.current.editor.setTheme(theme === 'dark' ? 'card-dark' : 'card-light');
    }
  }, [theme]);
  
  return (
    <div className="rounded-lg overflow-hidden">
      <Editor
        height={editorHeight}
        defaultLanguage="vb"
        value={code}
        onMount={handleEditorDidMount}
        theme={theme === "dark" ? "card-dark" : "card-light"}
        options={{
          readOnly: true,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          fontSize: 16,
          lineHeight: 23,
          padding: { top: 16, bottom: 16 },
          lineNumbers: "on",
          renderLineHighlight: "none",
          scrollbar: {
            alwaysConsumeMouseWheel: false,
          },
        }}
      />
    </div>
  );
};
